import { useState, useEffect, useRef } from "react";
import { trackEvent } from "../../utils/analytics";

// ─── DATA ───────────────────────────────────────────────────────────────────

const SERVICES = [
  {
    num: "01",
    title: "Resolve",
    tag: "Clarity before code",
    desc: "We start with your business goals, not your backlog. Every project begins with a strategic audit — understanding where you are, where you need to be, and the exact path to get there.",
  },
  {
    num: "02",
    title: "Renovate",
    tag: "Built to scale",
    desc: "Clean architecture. Scalable foundations. We engineer your product to grow with your business — not just to ship, but to support the next 100,000 users without breaking.",
  },
  {
    num: "03",
    title: "Innovate",
    tag: "Beyond expectations",
    desc: "We bring ideas you haven't thought of yet. Features that reduce churn, increase revenue, and create real competitive advantage. We think like a co-founder, not a contractor.",
  },
  {
    num: "04",
    title: "Results",
    tag: "Software that earns",
    desc: "Every decision we make has a business case behind it. We measure success in revenue generated, users retained, and growth achieved — not tickets closed.",
  },
];

const PROCESS = [
  {
    num: "01",
    title: "Discovery Call",
    desc: "We understand your vision, your pain points, and what you need to achieve.",
  },
  {
    num: "02",
    title: "Product Scoping",
    desc: "MVP scope, architecture, and success metrics defined together.",
  },
  {
    num: "03",
    title: "Wireframing",
    desc: "Visual blueprints before a single line of code is written.",
  },
  {
    num: "04",
    title: "Development",
    desc: "Clean, documented, scalable code. Weekly demos. Zero surprises.",
  },
  {
    num: "05",
    title: "Deployment",
    desc: "Production-ready launch with analytics, monitoring, and CI/CD.",
  },
  {
    num: "06",
    title: "30-Day Support",
    desc: "We stay with you post-launch. Because shipping is just the beginning.",
  },
];

const OFFERINGS = [
  {
    icon: "⬡",
    title: "Web Apps",
    stack: "React · Vue · Nuxt",
    desc: "Fast, scalable web applications built for real users. From internal tools to customer-facing platforms.",
  },
  {
    icon: "◎",
    title: "Mobile Apps",
    stack: "React Native",
    desc: "Cross-platform iOS & Android apps with native performance. One codebase, two stores, zero compromise.",
  },
  {
    icon: "◈",
    title: "MVP Builds",
    stack: "Idea → Launch",
    desc: "Go from napkin sketch to live product in 8–10 weeks. Scoped lean, built right, ready to raise or sell.",
  },
  {
    icon: "⬟",
    title: "API & Backend",
    stack: "Node · Express · MongoDB",
    desc: "Robust APIs and server architecture that hold up under load. Built to integrate, scale, and survive.",
  },
  {
    icon: "◇",
    title: "UI/UX Design",
    stack: "Figma · Prototyping",
    desc: "Interfaces that convert. We design with user psychology and business goals in mind — not just aesthetics.",
  },
  {
    icon: "◉",
    title: "Tech Consulting",
    stack: "Strategy · Audits",
    desc: "Not sure what to build or how to fix it? We audit your stack, challenge your assumptions, and give you a clear path.",
  },
  {
    icon: "⬠",
    title: "Maintenance & Support",
    stack: "Ongoing · Retainer",
    desc: "Post-launch care that keeps your product healthy. Bug fixes, updates, monitoring — we don't disappear after delivery.",
  },
  {
    icon: "◆",
    title: "AI Integrations",
    stack: "OpenAI · Claude · Custom",
    desc: "Embed AI into your product where it actually makes sense. Automations, assistants, and smart features that earn their keep.",
  },
];

const STATS = [
  { value: "6+", label: "Years of craft" },
  { value: "8–10", label: "Weeks to MVP" },
  { value: "40%", label: "Dev time reduced" },
  { value: "<12hr", label: "Response time" },
];

const TESTIMONIALS = [
  {
    name: "Ehsan Umer",
    role: "E-commerce Web App · ⭐ 5.0 Upwork Verified",
    text: "Muhammad Maaz is highly recommended! Great to work with, excellent communication, and timely delivery of efficient, high-quality code for my full-stack web app. He exceeded all expectations, and I'll definitely work with him again.",
  },
  {
    name: "Ammar",
    role: "Full-Stack Web App · Client Feedback",
    text: "After being let down by two developers before this, I wasn't very hopeful. But Maaz actually came through. The site looked amazing, the code was solid, and he delivered exactly what I asked for. Will definitely work with him again.",
  },
];

const NAV_ITEMS = [
  { label: "Services", id: "services" },
  { label: "Process", id: "process" },
  { label: "About", id: "about" },
  { label: "Contact", id: "contact" },
];

const MARQUEE_WORDS = ["Resolve", "·", "Renovate", "·", "Innovate", "·", "Results", "·"];

// ─── THEME ──────────────────────────────────────────────────────────────────

const C = {
  heroBlack: "#0A0F1A",
  heroBlack2: "#0D1321",
  navy: "#344F6B",
  navyLight: "#4A6A8A",
  navyDim: "rgba(52,79,107,0.15)",
  white: "#ffffff",
  offWhite: "#F7F9FC",
  lightBg: "#EEF3F8",
  text: "#0F1923",
  textMuted: "#4A5568",
  textFaint: "#718096",
  border: "rgba(52,79,107,0.12)",
  borderMid: "rgba(52,79,107,0.2)",
};

// ─── HELPERS ────────────────────────────────────────────────────────────────

function scrollTo(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  if (id === "contact") {
    trackEvent("StartProject");
  }
}

// ─── GLOBAL CSS ─────────────────────────────────────────────────────────────

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Ubuntu:ital,wght@0,300;0,400;0,500;0,700;1,300;1,400&family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { overflow-x: hidden; }
  ::-webkit-scrollbar { width: 2px; }
  ::-webkit-scrollbar-track { background: #EEF3F8; }
  ::-webkit-scrollbar-thumb { background: #344F6B; }

  /* Cursor */
  .cursor-dot { position: fixed; width: 6px; height: 6px; background: #344F6B; border-radius: 50%; pointer-events: none; z-index: 9999; transform: translate(-50%,-50%); }
  .cursor-ring { position: fixed; width: 30px; height: 30px; border: 1px solid rgba(52,79,107,0.4); border-radius: 50%; pointer-events: none; z-index: 9998; transform: translate(-50%,-50%); transition: width .25s, height .25s, border-color .25s; }
  .cursor-ring.hovered { width: 52px; height: 52px; border-color: #344F6B; }
  @media (max-width: 768px) { .cursor-dot, .cursor-ring { display: none; } }

  /* Nav */
  .nav-link { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 12px; font-weight: 500; letter-spacing: 0.15em; text-transform: uppercase; color: rgba(255,255,255,0.6); cursor: pointer; transition: color 0.25s; text-decoration: none; background: none; border: none; padding: 0; outline: none; }
  .nav-link:hover { color: #ffffff; }
  .nav-link:focus, .nav-link:active { outline: none; color: rgba(255,255,255,0.6); }
  .nav-link.scrolled { color: #94A3B8; }
  .nav-link.scrolled:hover { color: #0F1923; }
  .nav-link.scrolled:focus, .nav-link.scrolled:active { outline: none; color: #94A3B8; }

  /* Hamburger */
  .hamburger { display: none; flex-direction: column; gap: 5px; cursor: pointer; background: none; border: none; padding: 4px; z-index: 600; }
  .hamburger span { display: block; width: 22px; height: 1.5px; background: #ffffff; transition: all 0.3s; transform-origin: center; }
  .hamburger.scrolled span { background: #0F1923; }
  .hamburger.open span:nth-child(1) { transform: translateY(6.5px) rotate(45deg); }
  .hamburger.open span:nth-child(2) { opacity: 0; transform: scaleX(0); }
  .hamburger.open span:nth-child(3) { transform: translateY(-6.5px) rotate(-45deg); }
  @media (max-width: 768px) {
    .hamburger { display: flex; }
    .desktop-nav { display: none !important; }
    .desktop-cta { display: none !important; }
  }

  /* Mobile menu */
  .mobile-menu { position: fixed; inset: 0; background: #0A0F1A; z-index: 490; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 40px; transform: translateX(100%); transition: transform 0.4s cubic-bezier(0.4,0,0.2,1); }
  .mobile-menu.open { transform: translateX(0); }
  .mobile-menu .nav-link { font-size: 13px; letter-spacing: 0.22em; color: rgba(255,255,255,0.7); }

  /* Buttons */
  .btn-primary { display: inline-flex; align-items: center; gap: 10px; background: #344F6B; color: #ffffff; border: none; padding: 16px 36px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 11px; font-weight: 600; letter-spacing: 0.15em; text-transform: uppercase; cursor: pointer; transition: all 0.3s; white-space: nowrap; border-radius: 2px; }
  .btn-primary:hover { background: #4A6A8A; }
  .btn-secondary { display: inline-flex; align-items: center; gap: 10px; background: transparent; color: rgba(255,255,255,0.8); border: 1px solid rgba(255,255,255,0.2); padding: 16px 36px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 11px; font-weight: 500; letter-spacing: 0.15em; text-transform: uppercase; cursor: pointer; transition: all 0.3s; white-space: nowrap; border-radius: 2px; }
  .btn-secondary:hover { border-color: rgba(255,255,255,0.5); color: #ffffff; }
  .btn-light { display: inline-flex; align-items: center; gap: 10px; background: transparent; color: #344F6B; border: 1px solid rgba(52,79,107,0.25); padding: 16px 36px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 11px; font-weight: 500; letter-spacing: 0.15em; text-transform: uppercase; cursor: pointer; transition: all 0.3s; white-space: nowrap; border-radius: 2px; }
  .btn-light:hover { border-color: #344F6B; background: rgba(52,79,107,0.05); }

  /* Section label */
  .section-label { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 0.28em; text-transform: uppercase; color: #344F6B; margin-bottom: 40px; display: flex; align-items: center; gap: 14px; }
  .section-label::before { content: ''; width: 20px; height: 2px; background: #344F6B; flex-shrink: 0; }

  /* Cards */
  .stat-card { padding: 36px 28px; border: 1px solid rgba(52,79,107,0.12); background: #ffffff; transition: border-color 0.3s, transform 0.3s; }
  .stat-card:hover { border-color: #344F6B; transform: translateY(-3px); }
  .testimonial-card { padding: 36px; border: 1px solid rgba(52,79,107,0.1); background: #ffffff; transition: all 0.3s; border-radius: 4px; }
  .testimonial-card:hover { border-color: rgba(52,79,107,0.35); transform: translateY(-4px); box-shadow: 0 12px 32px rgba(52,79,107,0.08); }

  /* Service rows */
  .service-row { display: grid; grid-template-columns: 64px 1fr 36px; padding: 32px 0; border-bottom: 1px solid rgba(52,79,107,0.1); cursor: pointer; transition: background 0.3s; }
  .service-row:first-child { border-top: 1px solid rgba(52,79,107,0.1); }
  .service-row:hover, .service-row.active { background: rgba(52,79,107,0.04); }

  /* Process */
  .process-item { display: grid; grid-template-columns: 52px 1fr; gap: 20px; padding: 24px 0; border-bottom: 1px solid rgba(52,79,107,0.1); transition: padding-left 0.3s; cursor: default; }
  .process-item:hover { padding-left: 10px; }
  .process-item:first-child { border-top: 1px solid rgba(52,79,107,0.1); }

  /* Marquee */
  .marquee-track { display: flex; gap: 64px; animation: marquee 28s linear infinite; width: max-content; }
  @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }

  /* Footer links */
  .footer-link { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 10px; letter-spacing: 0.15em; text-transform: uppercase; color: rgba(255,255,255,0.4); text-decoration: none; transition: color 0.2s; cursor: pointer; background: none; border: none; padding: 0; }
  .footer-link:hover { color: rgba(255,255,255,0.85); }

  /* Tag pill */
  .tag-pill { display: inline-block; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 10px; font-weight: 500; letter-spacing: 0.15em; text-transform: uppercase; color: #344F6B; border: 1px solid rgba(52,79,107,0.25); padding: 4px 10px; border-radius: 100px; white-space: nowrap; }

  /* Grain overlay */
  .grain { position: fixed; inset: 0; pointer-events: none; z-index: 1000; opacity: 0.02; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E"); background-size: 128px 128px; }

  /* ─── RESPONSIVE ─── */
  @media (max-width: 1024px) {
    .two-col { grid-template-columns: 1fr !important; gap: 48px !important; }
    .four-col { grid-template-columns: repeat(2, 1fr) !important; }
    .three-col { grid-template-columns: repeat(2, 1fr) !important; }
    .section-h-pad { padding-left: 40px !important; padding-right: 40px !important; }
  }
  @media (max-width: 768px) {
    .section-h-pad { padding-left: 24px !important; padding-right: 24px !important; }
    .section-v-pad { padding-top: 60px !important; padding-bottom: 60px !important; }
    .two-col { grid-template-columns: 1fr !important; gap: 32px !important; }
    .four-col { grid-template-columns: repeat(2, 1fr) !important; }
    .three-col { grid-template-columns: 1fr !important; }
    .hero-section { padding: 100px 24px 64px !important; min-height: auto !important; }
    .hero-btns { flex-direction: column !important; width: 100%; }
    .hero-btns button { width: 100%; justify-content: center; }
    .hide-mobile { display: none !important; }
    .founder-photo { display: none !important; }
    .service-row { grid-template-columns: 40px 1fr 24px !important; padding: 18px 0 !important; }
    .tag-pill { display: none; }
    .cta-btns { flex-direction: column !important; }
    .cta-btns button { width: 100%; justify-content: center; }
    .stat-card { padding: 22px 16px !important; }
    .skills-row { gap: 24px !important; flex-wrap: wrap; }
    .process-item { grid-template-columns: 36px 1fr !important; gap: 10px !important; }
    .process-item:hover { padding-left: 0 !important; }
  }
    @keyframes marqueeReverse {
  from { transform: translateX(-50%); }
  to { transform: translateX(0); }
}
`;

// ─── COMPONENTS ─────────────────────────────────────────────────────────────

function LogoIcon({ light = false, size = 36 }) {
  const fg = light ? "rgba(255,255,255,0.9)" : "#344F6B";
  const accent = light ? "#ffffff" : "#344F6B";
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect x="1" y="1" width="46" height="46" rx="5.5" fill="none" stroke={fg} strokeWidth="1.5" />
      <line x1="12" y1="11" x2="12" y2="37" stroke={fg} strokeWidth="2.5" strokeLinecap="round" />
      <path d="M12 11 Q12 11 20 11 Q28 11 28 18.5 Q28 26 20 26 L12 26" stroke={fg} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="20" y1="26" x2="29" y2="37" stroke={fg} strokeWidth="2.5" strokeLinecap="round" />
      <polyline points="29,37 33,31 37,33 41,24" stroke={accent} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="41" cy="24" r="2" fill={accent} />
    </svg>
  );
}

function Cursor({ mousePos, cursorPos, isHovering }) {
  return (
    <>
      <div className="cursor-dot" style={{ left: mousePos.x, top: mousePos.y }} />
      <div className={`cursor-ring ${isHovering ? "hovered" : ""}`} style={{ left: cursorPos.x, top: cursorPos.y }} />
    </>
  );
}

function Navbar({ scrolled, setIsHovering }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const handleNav = (id) => {
    setMenuOpen(false);
    setTimeout(() => scrollTo(id), menuOpen ? 420 : 0);
  };

  return (
    <>
      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 500,
          padding: "24px 64px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: scrolled ? "rgba(255,255,255,0.97)" : "transparent",
          backdropFilter: scrolled ? "blur(20px)" : "none",
          borderBottom: scrolled ? `1px solid ${C.border}` : "none",
          transition: "all 0.4s",
        }}
        className="section-h-pad"
      >
        {/* Brand */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            cursor: "pointer",
          }}
          onClick={() => scrollTo("hero")}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <LogoIcon light={!scrolled} size={32} />
          <span
            style={{
              fontFamily: "'Ubuntu', sans-serif",
              fontWeight: 700,
              fontSize: 22,
              letterSpacing: "0.02em",
              color: scrolled ? C.text : "#ffffff",
            }}
          >
            Resova
            <span
              style={{
                color: scrolled ? C.navy : "rgba(255,255,255,0.55)",
                fontWeight: 400,
              }}
            >
              labs
            </span>
          </span>
        </div>

        {/* Desktop Nav */}
        <div className="desktop-nav" style={{ display: "flex", gap: 36 }}>
          {NAV_ITEMS.map(({ label, id }) => (
            <button key={id} className={`nav-link ${scrolled ? "scrolled" : ""}`} onClick={() => scrollTo(id)} onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
              {label}
            </button>
          ))}
        </div>

        {/* CTA */}
        <button
          className="btn-primary desktop-cta"
          style={{ padding: "10px 24px", fontSize: 10 }}
          onClick={() => scrollTo("contact")}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          Start a Project
        </button>

        {/* Hamburger */}
        <button className={`hamburger ${menuOpen ? "open" : ""} ${scrolled ? "scrolled" : ""}`} onClick={() => setMenuOpen((v) => !v)} aria-label="Toggle menu">
          <span />
          <span />
          <span />
        </button>
      </nav>

      <div className={`mobile-menu ${menuOpen ? "open" : ""}`}>
        {NAV_ITEMS.map(({ label, id }) => (
          <button key={id} className="nav-link" onClick={() => handleNav(id)}>
            {label}
          </button>
        ))}
        <button className="btn-primary" style={{ marginTop: 16 }} onClick={() => handleNav("contact")}>
          Start a Project
        </button>
      </div>
    </>
  );
}

function Hero({ setIsHovering }) {
  return (
    <section
      id="hero"
      className="hero-section"
      style={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${C.heroBlack} 0%, ${C.heroBlack2} 50%, #1a2a3a 100%)`,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: "120px 64px 80px",
        position: "relative",
        overflow: "hidden",
        textAlign: "center",
      }}
    >
      {/* Background grid */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: "linear-gradient(rgba(52,79,107,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(52,79,107,0.08) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          pointerEvents: "none",
        }}
      />

      {/* Big R watermark */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          fontFamily: "'Ubuntu', sans-serif",
          fontWeight: 700,
          fontSize: "clamp(200px, 40vw, 520px)",
          color: "rgba(52,79,107,0.05)",
          lineHeight: 1,
          userSelect: "none",
          pointerEvents: "none",
          letterSpacing: "-0.05em",
        }}
      >
        R
      </div>

      {/* Est tag */}
      <div
        className="hide-mobile"
        style={{
          position: "absolute",
          top: 120,
          right: 64,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 10,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.3)",
          display: "flex",
          flexDirection: "column",
          gap: 4,
          textAlign: "right",
        }}
      >
        <span>Est. 2026</span>
        <span>Karachi</span>
      </div>

      {/* Content — centered */}
      <div
        style={{
          maxWidth: 780,
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Badge */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            border: "1px solid rgba(52,79,107,0.4)",
            borderRadius: 100,
            padding: "6px 16px",
            marginBottom: 40,
          }}
        >
          <div
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "#344F6B",
            }}
          />
          <span
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.6)",
            }}
          >
            Built for Growth · Delivered with Craft
          </span>
        </div>

        {/* Heading */}
        <h1
          style={{
            fontFamily: "'Ubuntu', sans-serif",
            fontWeight: 700,
            fontSize: "clamp(48px, 8vw, 104px)",
            lineHeight: 1.05,
            letterSpacing: "-0.02em",
            marginBottom: 32,
            color: "#ffffff",
          }}
        >
          <span style={{ color: "rgba(255,255,255,0.4)" }}>We build software</span>
          <br />
          that grows your business.
        </h1>

        {/* Subtext */}
        <p
          style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: "clamp(14px, 1.5vw, 17px)",
            fontWeight: 300,
            lineHeight: 1.8,
            color: "rgba(255,255,255,0.55)",
            maxWidth: 560,
            marginBottom: 40,
          }}
        >
          We help founders, startups, and businesses turn ideas into real software — fast, clean, and built to grow. Idea to launch in 8–10 weeks.
        </p>

        {/* Divider */}
        <div
          style={{
            width: "100%",
            maxWidth: 560,
            height: 1,
            background: "rgba(255,255,255,0.08)",
            marginBottom: 40,
          }}
        />

        {/* Buttons */}
        <div
          className="hero-btns"
          style={{
            display: "flex",
            gap: 12,
            justifyContent: "center",
          }}
        >
          <button className="btn-primary" onClick={() => scrollTo("contact")} onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
            Start a convo →
          </button>
          <button className="btn-secondary" onClick={() => scrollTo("process")} onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
            Our Process
          </button>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="hide-mobile"
        style={{
          position: "absolute",
          bottom: 40,
          right: 64,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
        }}
      >
        <div
          style={{
            width: 1,
            height: 56,
            background: "linear-gradient(to bottom, transparent, rgba(52,79,107,0.6))",
          }}
        />
        <span
          style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 9,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: "rgba(52,79,107,0.7)",
            writingMode: "vertical-rl",
          }}
        >
          Scroll
        </span>
      </div>
    </section>
  );
}

function Marquee() {
  const words = [...Array(4)].flatMap(() => MARQUEE_WORDS);
  return (
    <div
      style={{
        borderTop: `1px solid ${C.border}`,
        borderBottom: `1px solid ${C.border}`,
        padding: "18px 0",
        overflow: "hidden",
        background: C.offWhite,
      }}
    >
      <div className="marquee-track">
        {words.map((word, i) => (
          <span
            key={i}
            style={{
              fontFamily: "'Ubuntu', sans-serif",
              fontSize: word === "·" ? 14 : 13,
              fontStyle: word !== "·" ? "italic" : "normal",
              fontWeight: word !== "·" ? 400 : 300,
              color: word === "·" ? C.navy : C.textFaint,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
            }}
          >
            {word}
          </span>
        ))}
      </div>
    </div>
  );
}

function Offerings({ setIsHovering }) {
  const [hovered, setHovered] = useState(null);
  return (
    <section id="services" className="section-h-pad section-v-pad" style={{ padding: "80px 64px 100px", background: C.white }}>
      <div className="section-label">What We Build</div>
      <div
        className="two-col"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 64,
          alignItems: "start",
          marginBottom: 56,
        }}
      >
        <h2
          style={{
            fontFamily: "'Ubuntu', sans-serif",
            fontWeight: 700,
            fontSize: "clamp(28px,4vw,48px)",
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
            color: C.text,
          }}
        >
          Eight ways we
          <br />
          <span style={{ color: C.navy, fontStyle: "italic", fontWeight: 400 }}>grow your business.</span>
        </h2>
        <p
          style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: "clamp(14px,1.4vw,16px)",
            fontWeight: 300,
            lineHeight: 1.8,
            color: C.textMuted,
            paddingTop: 8,
          }}
        >
          From your first idea to your next growth milestone — we cover the full stack of what modern software businesses need to move fast and build right.
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 1,
          background: C.border,
        }}
        className="four-col"
      >
        {OFFERINGS.map((s, i) => (
          <div
            key={i}
            style={{
              background: hovered === i ? C.offWhite : C.white,
              padding: "32px 28px",
              transition: "background 0.25s",
              cursor: "default",
              position: "relative",
              overflow: "hidden",
            }}
            onMouseEnter={() => {
              setHovered(i);
              setIsHovering(true);
            }}
            onMouseLeave={() => {
              setHovered(null);
              setIsHovering(false);
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 2,
                background: C.navy,
                transform: hovered === i ? "scaleX(1)" : "scaleX(0)",
                transition: "transform 0.3s ease",
                transformOrigin: "left",
              }}
            />
            <div
              style={{
                fontFamily: "'Ubuntu', sans-serif",
                fontSize: 22,
                color: hovered === i ? C.navy : "rgba(52,79,107,0.25)",
                marginBottom: 16,
                transition: "color 0.25s",
                lineHeight: 1,
              }}
            >
              {s.icon}
            </div>
            <div
              style={{
                fontFamily: "'Ubuntu', sans-serif",
                fontWeight: 700,
                fontSize: 16,
                color: C.text,
                marginBottom: 4,
              }}
            >
              {s.title}
            </div>
            <div
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: C.navy,
                marginBottom: 14,
              }}
            >
              {s.stack}
            </div>
            <div
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 12,
                fontWeight: 300,
                lineHeight: 1.7,
                color: C.textFaint,
              }}
            >
              {s.desc}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: 48,
          paddingTop: 32,
          borderTop: `1px solid ${C.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        <p
          style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 13,
            fontWeight: 300,
            color: C.textFaint,
          }}
        >
          Not sure what you need? We'll help you figure it out.
        </p>
        <button className="btn-light" onClick={() => scrollTo("contact")} onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
          Start a convo →
        </button>
      </div>
    </section>
  );
}

function Stats({ setIsHovering }) {
  return (
    <section className="section-h-pad section-v-pad" style={{ padding: "90px 64px", background: C.offWhite }}>
      <div
        className="four-col"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4,1fr)",
          gap: 1,
          background: C.border,
        }}
      >
        {STATS.map((s, i) => (
          <div
            key={i}
            className="stat-card"
            style={{
              background: C.white,
              padding: "40px 32px",
              border: "none",
            }}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <div
              style={{
                fontFamily: "'Ubuntu', sans-serif",
                fontWeight: 700,
                fontSize: "clamp(36px,4vw,52px)",
                lineHeight: 1,
                color: C.navy,
                letterSpacing: "-0.02em",
                marginBottom: 10,
              }}
            >
              {s.value}
            </div>
            <div
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: C.textFaint,
              }}
            >
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Services({ setIsHovering }) {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActive((p) => (p + 1) % SERVICES.length), 3500);
    return () => clearInterval(t);
  }, []);
  return (
    <section id="framework" className="section-h-pad section-v-pad" style={{ padding: "80px 64px 100px", background: C.white }}>
      <div className="section-label">Our Framework</div>
      <div
        className="two-col"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 80,
          alignItems: "start",
        }}
      >
        <div>
          <h2
            style={{
              fontFamily: "'Ubuntu', sans-serif",
              fontWeight: 700,
              fontSize: "clamp(28px,4vw,48px)",
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
              marginBottom: 20,
              color: C.text,
            }}
          >
            Four principles.
            <br />
            <span style={{ color: C.navy, fontStyle: "italic", fontWeight: 400 }}>One outcome.</span>
          </h2>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: "clamp(14px,1.4vw,16px)",
              fontWeight: 300,
              lineHeight: 1.8,
              color: C.textMuted,
              maxWidth: 380,
            }}
          >
            Every engagement runs through our four-stage framework — not a template, but a discipline built around one goal: your business grows because of what we built together.
          </p>
        </div>
        <div>
          {SERVICES.map((s, i) => (
            <div
              key={i}
              className={`service-row ${active === i ? "active" : ""}`}
              onClick={() => setActive(i)}
              onMouseEnter={() => {
                setIsHovering(true);
                setActive(i);
              }}
              onMouseLeave={() => setIsHovering(false)}
            >
              <span
                style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.15em",
                  color: "rgba(52,79,107,0.3)",
                  paddingTop: 4,
                  transition: "color 0.3s",
                }}
              >
                {s.num}
              </span>
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    marginBottom: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'Ubuntu', sans-serif",
                      fontWeight: 700,
                      fontSize: 20,
                      color: C.text,
                    }}
                  >
                    {s.title}
                  </span>
                  <span className="tag-pill">{s.tag}</span>
                </div>
                <p
                  style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 13,
                    lineHeight: 1.7,
                    color: C.textFaint,
                    fontWeight: 300,
                    maxHeight: active === i ? 100 : 0,
                    overflow: "hidden",
                    transition: "max-height 0.4s ease, opacity 0.3s",
                    opacity: active === i ? 1 : 0,
                  }}
                >
                  {s.desc}
                </p>
              </div>
              <div style={{ textAlign: "right", paddingTop: 4 }}>
                <span
                  style={{
                    fontSize: 16,
                    color: active === i ? C.navy : "rgba(52,79,107,0.2)",
                    transition: "color 0.3s, transform 0.3s",
                    display: "inline-block",
                    transform: active === i ? "rotate(0deg)" : "rotate(-45deg)",
                  }}
                >
                  →
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Process({ setIsHovering }) {
  const [hovered, setHovered] = useState(null);
  return (
    <section id="process" className="section-h-pad section-v-pad" style={{ padding: "80px 64px 100px", background: C.offWhite }}>
      <div className="section-label">How We Work</div>
      <div className="two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80 }}>
        <div>
          <h2
            style={{
              fontFamily: "'Ubuntu', sans-serif",
              fontWeight: 700,
              fontSize: "clamp(28px,4vw,48px)",
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
              color: C.text,
            }}
          >
            Idea to MVP
            <br />
            <span style={{ color: C.navy, fontStyle: "italic", fontWeight: 400 }}>in 8–10 weeks.</span>
          </h2>
          <div
            style={{
              width: 36,
              height: 2,
              background: C.navy,
              margin: "24px 0",
            }}
          />
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: "clamp(14px,1.4vw,16px)",
              fontWeight: 300,
              lineHeight: 1.8,
              color: C.textMuted,
            }}
          >
            We don't start coding on day one. We start by understanding your business model, your users, and what success actually means for your revenue. Then we build with that as our north star.
          </p>
        </div>
        <div>
          {PROCESS.map((item, i) => (
            <div
              key={i}
              className="process-item"
              onMouseEnter={() => {
                setIsHovering(true);
                setHovered(i);
              }}
              onMouseLeave={() => {
                setIsHovering(false);
                setHovered(null);
              }}
            >
              <span
                style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.15em",
                  color: hovered === i ? C.navy : "rgba(52,79,107,0.3)",
                  transition: "color 0.2s",
                  paddingTop: 2,
                }}
              >
                {item.num}
              </span>
              <div>
                <div
                  style={{
                    fontFamily: "'Ubuntu', sans-serif",
                    fontWeight: 500,
                    fontSize: "clamp(14px,1.4vw,16px)",
                    marginBottom: 5,
                    color: hovered === i ? C.text : "#2A3A4A",
                  }}
                >
                  {item.title}
                </div>
                <div
                  style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 12,
                    lineHeight: 1.7,
                    color: C.textFaint,
                    fontWeight: 300,
                  }}
                >
                  {item.desc}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div
        style={{
          marginTop: 48,
          paddingTop: 32,
          borderTop: `1px solid ${C.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        <p
          style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 13,
            fontWeight: 300,
            color: C.textFaint,
          }}
        >
          Ready to kick off your project?
        </p>
        <button className="btn-light" onClick={() => scrollTo("contact")} onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
          Let's talk →
        </button>
      </div>
    </section>
  );
}

function Founder({ setIsHovering }) {
  return (
    <section id="about" className="section-h-pad section-v-pad" style={{ padding: "80px 64px 100px", background: C.white }}>
      <div className="section-label">About Us</div>
      <div
        className="two-col"
        style={{
          display: "grid",
          gridTemplateColumns: "1.5fr 1fr",
          gap: 60,
          alignItems: "center",
        }}
      >
        <div>
          <h2
            style={{
              fontFamily: "'Ubuntu', sans-serif",
              fontWeight: 700,
              fontSize: "clamp(24px,3vw,42px)",
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
              marginBottom: 28,
              color: C.text,
            }}
          >
            We ask why before
            <br />
            <span style={{ color: C.navy, fontStyle: "italic", fontWeight: 400 }}>we write a line of code.</span>
          </h2>

          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: "clamp(13px,1.3vw,15px)",
              fontWeight: 300,
              lineHeight: 1.9,
              color: C.textMuted,
              marginBottom: 10,
              textAlign: "left",
            }}
          >
            Resovate Labs was started by a developer who got tired of seeing founders get burned. By agencies that overpromise, freelancers that ghost, and code that falls apart six months after
            launch. We're a small focused team based in Karachi six years of building real products, mobile apps, web platforms, full-stack systems, used by real people.
          </p>

          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: "clamp(13px,1.3vw,15px)",
              fontWeight: 300,
              lineHeight: 1.9,
              color: C.textMuted,
              marginBottom: 20,
              textAlign: "left",
            }}
          >
            Every decision we make has a business reason behind it. What moves the needle for your revenue, your users, your growth. The tech is just how we get there.
            <br />
            Every project starts with one question: what does success look like for your business? Not lines of code. Not tickets closed. We build around that answer.
          </p>

          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: "clamp(13px,1.3vw,15px)",
              fontWeight: 400,
              lineHeight: 1.9,
              color: C.text,
              marginBottom: 36,
              borderLeft: `2px solid ${C.navy}`,
              paddingLeft: 16,
            }}
          >
            We're selective about who we work with. Not because we're too busy, but because we genuinely care about the outcome.
          </p>

          <div
            className="skills-row"
            style={{
              display: "flex",
              gap: 40,
              paddingTop: 28,
              borderTop: `1px solid ${C.border}`,
            }}
          >
            {[
              ["MERN Stack", "6 Years"],
              ["React Native", "5 Years"],
              ["Team Lead", "2 Years"],
            ].map(([skill, years], i) => (
              <div key={i}>
                <div
                  style={{
                    fontFamily: "'Ubuntu', sans-serif",
                    fontWeight: 700,
                    fontSize: 22,
                    color: C.navy,
                  }}
                >
                  {years}
                </div>
                <div
                  style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: "0.15em",
                    color: C.textFaint,
                    textTransform: "uppercase",
                    marginTop: 4,
                  }}
                >
                  {skill}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div
          className="founder-photo"
          style={{
            width: "100%",
            aspectRatio: "3/4",
            maxWidth: 320,
            background: `linear-gradient(135deg, ${C.heroBlack} 0%, #1a2f4a 100%)`,
            border: `1px solid ${C.border}`,
            position: "relative",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              fontFamily: "'Ubuntu', sans-serif",
              fontWeight: 700,
              fontSize: 140,
              color: "rgba(52,79,107,0.15)",
            }}
          >
            M
          </span>
          <div
            style={{
              position: "absolute",
              bottom: 24,
              left: 24,
              right: 24,
              background: "rgba(10,15,26,0.92)",
              padding: "16px 20px",
              borderTop: `2px solid ${C.navy}`,
            }}
          >
            <div
              style={{
                fontFamily: "'Ubuntu', sans-serif",
                fontSize: 17,
                fontWeight: 500,
                color: "#ffffff",
              }}
            >
              Muhammad Maaz
            </div>
            <div
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.15em",
                color: "rgba(52,79,107,0.8)",
                textTransform: "uppercase",
                marginTop: 4,
              }}
            >
              Founder & Lead Engineer
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
function Testimonials({ setIsHovering }) {
  return (
    <section className="section-h-pad section-v-pad" style={{ padding: "80px 64px 100px", background: C.offWhite }}>
      <div className="section-label">Testimonials</div>
      <h2
        style={{
          fontFamily: "'Ubuntu', sans-serif",
          fontWeight: 700,
          fontSize: "clamp(24px,3.5vw,44px)",
          lineHeight: 1.15,
          letterSpacing: "-0.02em",
          marginBottom: 56,
          maxWidth: 480,
          color: C.text,
        }}
      >
        What our clients
        <br />
        <span style={{ color: C.navy, fontStyle: "italic", fontWeight: 400 }}>have to say.</span>
      </h2>
      <div
        className="three-col"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3,1fr)",
          gap: 20,
        }}
      >
        {TESTIMONIALS.map((t, i) => (
          <div key={i} className="testimonial-card" onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
            <div
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 300,
                fontSize: "clamp(13px,1.3vw,15px)",
                lineHeight: 1.85,
                color: C.textMuted,
                fontStyle: "italic",
                marginBottom: 28,
              }}
            >
              "{t.text}"
            </div>
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 18 }}>
              <div
                style={{
                  fontFamily: "'Ubuntu', sans-serif",
                  fontSize: 15,
                  fontWeight: 500,
                  color: C.text,
                }}
              >
                {t.name}
              </div>
              <div
                style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.15em",
                  color: C.navy,
                  textTransform: "uppercase",
                  marginTop: 3,
                }}
              >
                {t.role}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Contact({ setIsHovering }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: "0de3fc4f-aa7b-41c6-b97a-2cfea477ca34",
          name: formData.name,
          email: formData.email,
          message: formData.message,
          botcheck: "",
        }),
      });
      const json = await res.json();
      if (json.success) {
        setStatus("success");
        setFormData({ name: "", email: "", message: "" });
      } else {
        setStatus("error");
      }
    } catch (err) {
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%",
    background: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: 2,
    padding: "14px 18px",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 13,
    fontWeight: 300,
    color: C.text,
    outline: "none",
    transition: "border-color 0.25s",
    boxSizing: "border-box",
  };

  const labelStyle = {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: C.textFaint,
    display: "block",
    marginBottom: 8,
    textAlign: "left",
  };

  return (
    <section id="contact" className="section-h-pad section-v-pad" style={{ padding: "80px 64px 100px", background: C.offWhite }}>
      <div className="section-label">Get in Touch</div>
      <div
        className="two-col"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 80,
          alignItems: "start",
        }}
      >
        {/* Left — info */}
        <div>
          <h2
            style={{
              fontFamily: "'Ubuntu', sans-serif",
              fontWeight: 700,
              fontSize: "clamp(28px,4vw,48px)",
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
              color: C.text,
              marginBottom: 20,
              textAlign: "left",
            }}
          >
            Let's build
            <br />
            <span style={{ color: C.navy, fontStyle: "italic", fontWeight: 400 }}>something great.</span>
          </h2>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: "clamp(13px,1.3vw,15px)",
              fontWeight: 300,
              lineHeight: 1.9,
              color: C.textMuted,
              maxWidth: 360,
              textAlign: "left",
            }}
          >
            Tell us about your project and we'll get back to you within 24 hours. No commitment, no pressure — just a conversation.
          </p>
        </div>

        {/* Right — form */}
        <div
          style={{
            background: C.white,
            border: `1px solid ${C.border}`,
            borderRadius: 4,
            padding: "40px 36px",
          }}
        >
          {status === "success" && (
            <div
              style={{
                background: "rgba(52,79,107,0.08)",
                border: `1px solid rgba(52,79,107,0.2)`,
                borderRadius: 2,
                padding: "14px 18px",
                marginBottom: 24,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 13,
                color: C.navy,
              }}
            >
              ✓ Message sent! We'll be in touch within 6 hours.
            </div>
          )}
          {status === "error" && (
            <div
              style={{
                background: "rgba(180,60,60,0.06)",
                border: "1px solid rgba(180,60,60,0.2)",
                borderRadius: 2,
                padding: "14px 18px",
                marginBottom: 24,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 13,
                color: "#b44040",
              }}
            >
              Something went wrong. Please try again.
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div>
              <label style={labelStyle}>Name</label>
              <input
                name="name"
                type="text"
                placeholder="Your name"
                value={formData.name}
                onChange={handleChange}
                required
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = C.navy)}
                onBlur={(e) => (e.target.style.borderColor = C.border)}
              />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input
                name="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={handleChange}
                required
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = C.navy)}
                onBlur={(e) => (e.target.style.borderColor = C.border)}
              />
            </div>
            <div>
              <label style={labelStyle}>Message</label>
              <textarea
                name="message"
                placeholder="Tell us about your project..."
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                style={{ ...inputStyle, resize: "vertical", minHeight: 120 }}
                onFocus={(e) => (e.target.style.borderColor = C.navy)}
                onBlur={(e) => (e.target.style.borderColor = C.border)}
              />
            </div>
            <button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={loading}
              style={{
                width: "100%",
                justifyContent: "center",
                opacity: loading ? 0.7 : 1,
                marginTop: 4,
              }}
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              {loading ? "Sending..." : "Send Message →"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="section-h-pad" style={{ background: "#080E14" }}>
      {/* Top — growth heading + contact details */}
      <div
        style={{
          padding: "72px 64px 56px",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 80,
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          position: "relative",
          overflow: "hidden",
        }}
        className="two-col"
      >
        {/* Watermark */}
        <div
          style={{
            position: "absolute",
            right: -20,
            bottom: -40,
            fontFamily: "'Ubuntu', sans-serif",
            fontWeight: 700,
            fontSize: "clamp(80px,18vw,260px)",
            color: "rgba(52,79,107,0.05)",
            lineHeight: 1,
            userSelect: "none",
            pointerEvents: "none",
            letterSpacing: "-0.05em",
          }}
        >
          RSL
        </div>

        {/* Left — heading */}
        <div style={{ position: "relative", zIndex: 1 }}>
          <h2
            style={{
              fontFamily: "'Ubuntu', sans-serif",
              fontWeight: 700,
              fontSize: "clamp(28px,4vw,52px)",
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              color: "#ffffff",
              marginBottom: 20,
            }}
          >
            Your next stage of
            <br />
            <span style={{ color: C.navy, fontStyle: "italic", fontWeight: 400 }}>growth starts here.</span>
          </h2>
          {/* <p
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              fontWeight: 300,
              lineHeight: 1.8,
              color: "rgba(255,255,255,0.35)",
              maxWidth: 340,
            }}
          >
            We're selective about who we work with — because we care about the
            outcome, not just the output.
          </p> */}
        </div>

        {/* Right — contact details */}
        <div
          style={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 24,
          }}
        >
          {[
            { label: "Email", value: "mmaazy03@gmail.com" },
            { label: "Phone", value: "+92 318 332 0178" },
            {
              label: "Based in",
              value: "Karachi, Pakistan · Serving US, UK & AU",
            },
          ].map(({ label, value }) => (
            <div key={label}>
              <div
                style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 9,
                  fontWeight: 600,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: "rgba(52,79,107,0.7)",
                  marginBottom: 6,
                }}
              >
                {label}
              </div>
              <div
                style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 300,
                  color: "rgba(255,255,255,0.55)",
                }}
              >
                {value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div
        style={{
          padding: "24px 64px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <LogoIcon light size={22} />
          <span
            style={{
              fontFamily: "'Ubuntu', sans-serif",
              fontWeight: 400,
              fontSize: 13,
              letterSpacing: "0.04em",
              color: "rgba(255,255,255,0.4)",
            }}
          >
            Resovalabs
          </span>
        </div>
        <div className="footer-links-row" style={{ display: "flex", gap: 32 }}>
          {NAV_ITEMS.map(({ label, id }) => (
            <button key={id} className="footer-link" onClick={() => scrollTo(id)}>
              {label}
            </button>
          ))}
        </div>
        <div
          style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 10,
            letterSpacing: "0.12em",
            color: "rgba(255,255,255,0.2)",
            textTransform: "uppercase",
          }}
        >
          © 2026 Resovalabs
        </div>
      </div>
    </footer>
  );
}

// ─── APP ────────────────────────────────────────────────────────────────────

export default function ResovalabsLanding() {
  const [scrolled, setScrolled] = useState(false);
  const [mousePos, setMousePos] = useState({ x: -100, y: -100 });
  const [cursorPos, setCursorPos] = useState({ x: -100, y: -100 });
  const [isHovering, setIsHovering] = useState(false);
  const cursorRef = useRef({ x: -100, y: -100 });
  const rafRef = useRef();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    const onMouse = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
      cursorRef.current = { x: e.clientX, y: e.clientY };
    };
    const animate = () => {
      setCursorPos((prev) => ({
        x: prev.x + (cursorRef.current.x - prev.x) * 0.1,
        y: prev.y + (cursorRef.current.y - prev.y) * 0.1,
      }));
      rafRef.current = requestAnimationFrame(animate);
    };
    window.addEventListener("scroll", onScroll);
    window.addEventListener("mousemove", onMouse);
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("mousemove", onMouse);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div
      style={{
        background: C.offWhite,
        color: C.text,
        minHeight: "100vh",
        overflowX: "hidden",
        cursor: "none",
      }}
    >
      <style>{GLOBAL_CSS}</style>
      <div className="grain" />
      <Cursor mousePos={mousePos} cursorPos={cursorPos} isHovering={isHovering} />
      <Navbar scrolled={scrolled} setIsHovering={setIsHovering} />
      <Hero setIsHovering={setIsHovering} />
      <Marquee />

      <Offerings setIsHovering={setIsHovering} />
      <Stats setIsHovering={setIsHovering} />
      <Services setIsHovering={setIsHovering} />
      <Process setIsHovering={setIsHovering} />
      <Founder setIsHovering={setIsHovering} />
      <Testimonials setIsHovering={setIsHovering} />
      <Contact setIsHovering={setIsHovering} />
      <Footer />
    </div>
  );
}
